import "../../config/courseTheme.css";
import CourseUnreadMessages from "../Courses/CourseUnreadMessages";
import { coursesEnabled, courseApiEnabled } from "../../config/courseFeatures";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { fetchUnreadMessageThreads } from "../../store";
import { useThunk } from "../../hooks/useThunk";
import showToast from "../../utils/toastNotifications";

const ROWS_PER_PAGE = 10;
const UNREAD_THREAD_COUNT_REFRESH_EVENT = "unread-thread-count-refresh";

const formatOrderId = (roomId) => roomId?.toString()?.replace(/^ID/, "") || "-";

const formatMessageDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const UnreadMessages = () => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const messageSource = ["orders", "courses"].includes(searchParams.get("source")) ? searchParams.get("source") : "all";
  const showOrders = !coursesEnabled || messageSource !== "courses";
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [threads, setThreads] = useState([]);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [doFetchUnreadMessages, isFetchingUnreadMessages] = useThunk(fetchUnreadMessageThreads);

  const fetchUnreadMessages = useCallback(async () => {
    const result = await doFetchUnreadMessages({ page, size: ROWS_PER_PAGE });

    if (result.success) {
      setThreads(result?.response?.data?.data || []);
      setTotalDocuments(result?.response?.data?.totalDocuments || 0);
    } else {
      showToast("error", result?.error?.message || "Error fetching unread messages");
    }
  }, [doFetchUnreadMessages, page]);

  useEffect(() => {
    setSearchParams(previous => { const next = new URLSearchParams(previous); next.set("page", page); return next; }, { replace: true });
    fetchUnreadMessages();
  }, [fetchUnreadMessages, page, setSearchParams]);

  useEffect(() => {
    window.addEventListener(UNREAD_THREAD_COUNT_REFRESH_EVENT, fetchUnreadMessages);

    return () => {
      window.removeEventListener(UNREAD_THREAD_COUNT_REFRESH_EVENT, fetchUnreadMessages);
    };
  }, [fetchUnreadMessages]);

  const totalPages = Math.max(1, Math.ceil(totalDocuments / ROWS_PER_PAGE));
  const openThread = (thread) => {
    navigate(`/thread?roomIdParam=${thread.roomId}&threadIdParam=${thread._id}`);
  };

  return (
    <div className="mt-24 flex w-full flex-col px-4 xl:w-[calc(100vw-256px)] xl:px-8">
      <div className="mb-4 flex flex-col gap-2">
        <p className="font-inter text-level-4 text-gray">
          {coursesEnabled ? "Unread order messages and course conversations." : "Unread conversations that need your attention."}
        </p>
      </div>

      {coursesEnabled && <div role="group" aria-label="Message source" className="course-message-sources mb-4 flex gap-2">
        {["all", "orders", "courses"].map(source => <button key={source} type="button" aria-pressed={messageSource === source} onClick={() => setSearchParams(previous => { const next = new URLSearchParams(previous); next.set("source", source); return next; })} className={`rounded-md border px-4 py-2  ${messageSource === source ? "text-white" : "bg-white"}`}>{source[0].toUpperCase() + source.slice(1)}</button>)}
      </div>}
      {coursesEnabled && courseApiEnabled && messageSource !== "orders" && <CourseUnreadMessages />}
      {coursesEnabled && !courseApiEnabled && messageSource === "courses" && <div className="rounded-lg border border-medium-gray bg-white p-8 text-center"><h2 className="font-semibold">No course conversations yet</h2><p className="mt-2 text-sm text-gray">Your course conversations will appear here.</p></div>}
      {showOrders && <h2 className="mb-3 font-semibold">Unread order conversations</h2>}
      <div hidden={!showOrders} className="min-h-[420px] overflow-hidden rounded-lg border border-medium-gray bg-white">
        {isFetchingUnreadMessages ? (
          <div className="flex h-[420px] items-center justify-center">
            <ClipLoader color="#60a5fa" loading size={36} speedMultiplier={0.5} />
          </div>
        ) : threads.length < 1 ? (
          <div className="flex h-[420px] flex-col items-center justify-center px-6 text-center">
            <h2 className="font-roboto text-[20px] font-semibold text-secondary">
              No unread messages
            </h2>
            <p className="mt-2 max-w-[420px] font-inter text-level-4 text-gray">
              New conversations will appear here when they need your attention.
            </p>
          </div>
        ) : (
          <div className="max-h-[calc(100vh-260px)] min-h-[420px] divide-y divide-medium-gray overflow-y-auto">
            {threads.map((thread) => (
              <div
                key={thread._id}
                role="button"
                tabIndex={0}
                onClick={() => openThread(thread)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openThread(thread);
                  }
                }}
                className="flex w-full cursor-pointer flex-col gap-3 px-4 py-4 text-left transition-colors hover:bg-light-blue focus:bg-light-blue focus:outline-none sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-inter text-level-3 font-semibold text-blue-shades">
                      Order ID: {formatOrderId(thread.roomId)}
                    </span>
                    <span className="rounded-full bg-soft-blue px-2 py-1 font-inter text-[11px] font-medium text-secondary">
                      {thread.orderStatus || "Order"}
                    </span>
                  </div>
                  <p className="mt-2 line-clamp-2 font-inter text-level-4 text-gray">
                    {thread.lastMessage?.content || "New message"}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3 self-start sm:self-center">
                  <span className="font-inter text-[12px] text-gray">
                    {formatMessageDate(thread.lastMessage?.createdAt)}
                  </span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      openThread(thread);
                    }}
                    className="rounded-md bg-primary px-4 py-2 font-inter text-[12px] font-semibold text-white transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    Read
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showOrders && totalPages > 1 && (
        <div className="mt-5 flex justify-center gap-3">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="rounded-md border border-medium-gray px-4 py-2 font-inter text-level-4 text-secondary disabled:opacity-50"
          >
            Previous
          </button>
          <span className="flex items-center font-inter text-level-4 text-gray">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            className="rounded-md border border-medium-gray px-4 py-2 font-inter text-level-4 text-secondary disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default UnreadMessages;
